import { useModal } from "@ebay/nice-modal-react";
import { isField, isObjectField, onFieldValueChange } from "@formily/core";
import { Field, FormConsumer, FormProvider, ObjectField, observer, useField, useFormEffects } from "@formily/react";
import { useState } from "react";
import { Card } from "react-bootstrap";
import Table from "react-bootstrap/Table";
import { FaCaretDown, FaCaretRight, FaEllipsisVertical, FaArrowRotateLeft } from "react-icons/fa6";
import { useParams } from "react-router-dom";
import { css } from "../../../styled-system/css";
import { Box, Center, Flex, Grid, HStack, VStack, Wrap } from "../../../styled-system/jsx";
import { Button, LinkButton } from "../../components/core/basic/Button";
import { ButtonGroup } from "../../components/core/basic/ButtonGroup";
import { PageContainer } from "../../components/core/basic/PageContainer";
import BreadcrumbDates from "../../components/core/blocks/BreadcrumbDates";
import Loading from "../../components/core/blocks/Loading";
import { ProductImageURL } from "../../components/custom/category/listings/ProductImage";
import { SelectItem } from "../../components/fields/base/Select";
import { Checkbox } from "../../components/fields/formily/Checkbox";
import { CurrencyInput, OffsetInput } from "../../components/fields/formily/NumberInput";
import { Select } from "../../components/fields/formily/Select";
import { DatesProvider } from "../../dates/DatesContext";
import { useFormily } from "../../hooks/useFormily";
import type { GVProduct } from "../../zodios/common/gvProductSchema";
import { useParentAsinPricingConfig } from "./api/useParentAsinPricingConfig";
import { ConstraintsManagementModal } from "./components/modals/ConstraintsManagement";
import { PlanSlider } from "./components/PricingSlider";
import { TextField } from "../../components/fields/formily/TextField";
import { MenuButton, MenuItem } from "../../components/core/basic/Menu";
import ExternalProductLink from "../../components/custom/merchandise/ExternalProductLink";
import { RadioGroup } from "../../components/fields/formily/RadioGroup";

interface Offset {
  amount: number;
  type: "percent" | "currency";
}

interface CommonPriceConfig {
  enabled: true;
  currentPrice: number;
  fbmOffset: Offset;
}

interface DisabledConfig {
  enabled: false;
  currentPrice: number;
}

type FixedPriceConfig = CommonPriceConfig & {
  priceType: "fixed";
  // temporaryUntil: string | null;
};

type DynamicPriceConfig = CommonPriceConfig & {
  priceType: "dynamic";
  sliderValue: number;
  minPrice: number;
  maxPrice: number;
  additionalConstraints: {
    targetType: "hero" | "external";
    target: string;
    constraintOn: "min" | "max";
    adjust: Offset;
  }[];
};

type FollowerPriceConfig = CommonPriceConfig & {
  priceType: "sameas";
  targetType: "hero" | "external";
  target: string;
  addStatsToLeader: boolean;
  adjustLeaderPrice: Offset;
};

type PriceConfig = DisabledConfig | FixedPriceConfig | DynamicPriceConfig | FollowerPriceConfig;

type InitialConfigType = {
  groupBy: string;
  heroAsin: string;
  template: string;
  initialSlider: number;
};

function getDefaultPriceConfig(initialConfig: InitialConfigType, referencePrice: number): PriceConfig {
  switch (initialConfig.template) {
    case "manual":
      return {
        enabled: true,
        priceType: "fixed",
        currentPrice: referencePrice,
        fbmOffset: {
          amount: 0,
          type: "currency",
        },
      };
    case "followHero":
      return {
        enabled: true,
        priceType: "sameas",
        currentPrice: referencePrice,
        fbmOffset: {
          amount: 0,
          type: "currency",
        },
        targetType: "hero",
        target: initialConfig.heroAsin,
        addStatsToLeader: false,
        adjustLeaderPrice: { amount: 0, type: "currency" },
      };
    case "constrainedByHero":
      return {
        enabled: true,
        priceType: "dynamic",
        currentPrice: referencePrice,
        fbmOffset: {
          amount: 0,
          type: "currency",
        },
        sliderValue: initialConfig.initialSlider,
        minPrice: referencePrice * 0.9,
        maxPrice: referencePrice * 1.1,
        additionalConstraints: [
          {
            adjust: { amount: -10, type: "percent" },
            constraintOn: "min",
            target: initialConfig.heroAsin,
            targetType: "hero",
          },
          {
            adjust: { amount: 10, type: "percent" },
            constraintOn: "max",
            target: initialConfig.heroAsin,
            targetType: "hero",
          },
        ],
      };
    case "independent":
      return {
        enabled: true,
        priceType: "dynamic",
        currentPrice: referencePrice,
        fbmOffset: {
          amount: 0,
          type: "currency",
        },
        sliderValue: initialConfig.initialSlider,
        minPrice: referencePrice * 0.9,
        maxPrice: referencePrice * 1.1,
        additionalConstraints: [],
      };
    default:
      throw new Error(`Invalid template ${initialConfig.template}`);
  }
}

export function ParentAsinManagementPage() {
  const [step, setStep] = useState<"initial" | "final">("initial");
  const { parentAsin } = useParams<{ parentAsin: string }>();
  const query = useParentAsinPricingConfig({ asin: parentAsin });

  const [initialConfig, setInitialConfig] = useState<InitialConfigType | null>(null);

  if (query.error) return <div>Error</div>;
  if (!query.isSuccess) return <Loading />;

  return (
    <DatesProvider>
      <BreadcrumbDates items={[]} />
      <PageContainer withBreadcrumb>
        <Card className={css({ flex: 1 })}>
          <Card.Body className={css({ display: "flex", gap: "small", flexDirection: "column", fontSize: "large" })}>
            <HStack>
              <ProductImageURL url={query.data.parent.image} width={100} height={100} />
              <VStack alignItems={"start"}>
                <Box fontSize={"x-large"}>{query.data.parent.title}</Box>
                <Box>
                  {`${query.data.parent.asin} (${query.data.products.length.toString()} skus)`}
                  <ExternalProductLink asin={query.data.parent.asin} />
                </Box>
              </VStack>
            </HStack>
            {step === "initial" && (
              <InitialConfig
                data={query.data}
                onApply={(values) => {
                  setInitialConfig(values);
                  setStep("final");
                }}
              />
            )}
            {step === "final" && <ParentAsinManagementForm initialConfig={initialConfig!} data={query.data} />}
          </Card.Body>
        </Card>
      </PageContainer>
    </DatesProvider>
  );
}

function InitialConfig({
  data,
  onApply,
}: {
  data: Exclude<ReturnType<typeof useParentAsinPricingConfig>["data"], undefined>;
  onApply: (config: InitialConfigType) => void;
}) {
  const form = useFormily({
    initialValues: {
      groupBy: "none",
      heroAsin: "none",
      template: "independent",
      initialSlider: 0,
    },
  });

  const variations = Object.entries(data.variationOptions)
    .filter(([, v]) => v.length > 1)
    .map(([k]) => k);
  return (
    <FormProvider form={form}>
      <FormConsumer>
        {() => (
          <Flex gap={"small"} direction={"column"} alignItems={"center"}>
            <h2>Select your pricing setup</h2>
            {variations.length > 1 && (
              <Flex gap={"small"} alignItems={"center"}>
                <label>Group ASINs by:</label>
                <Field name={"groupBy"} hidden={variations.length === 1}>
                  {(field) => (
                    <ButtonGroup
                      value={field.value}
                      onSelect={field.onInput}
                      buttons={[
                        { label: "None", value: "none" },
                        ...variations.map((k) => ({
                          label: k,
                          value: k,
                        })),
                      ]}
                    />
                  )}
                </Field>
              </Flex>
            )}
            <Field
              name={"template"}
              title={"Choose your use case"}
              component={[RadioGroup]}
              dataSource={[
                { label: "I want all products to find their best price independently", value: "independent" },
                {
                  label: "I have a hero product, and want others to rigidly base their price on it",
                  value: "followHero",
                },
                {
                  label:
                    "I have a hero product, and want others to find their own best price, while never exceeding the hero",
                  value: "constrainedByHero",
                },
                { label: "I want to set everything up from scratch", value: "manual" },
              ]}
              reactions={(field) => {
                field.query(".heroAsin").take((heroAsinField) => {
                  heroAsinField.hidden = field.value !== "followHero" && field.value !== "constrainedByHero";
                });
                field.query(".initialSlider").take((sliderField) => {
                  sliderField.hidden = !["independent", "constrainedByHero"].includes(field.value);
                });
              }}
            />
            <Field
              name={"heroAsin"}
              title={"Choose Hero"}
              component={[
                Select,
                {
                  className: css({ width: "50%" }),
                  itemRenderer: ([asin, products]: [string, GVProduct[]]) => (
                    <SelectItem id={asin}>{`${asin} - ${products[0]!.product_title}`}</SelectItem>
                  ),
                },
              ]}
              dataSource={Object.entries(data.by_asin)}
            />
            <Box width="70%" minWidth={"250px"} padding={"0 25px"}>
              <Field name="initialSlider" hidden>
                {(field) => <PlanSlider planMode={field.value} setPlanMode={field.onInput} />}
              </Field>
            </Box>

            <Button onPress={async () => await form.submit(onApply)}>Proceed</Button>
          </Flex>
        )}
      </FormConsumer>
    </FormProvider>
  );
}

function resolveInitialConfigToFormValues(
  initialConfig: InitialConfigType,
  data: Exclude<ReturnType<typeof useParentAsinPricingConfig>["data"], undefined>,
) {
  const result = {
    groupConfig: {} as Record<string, PriceConfig>,
    asinConfig: {} as Record<string, PriceConfig>,
    skuConfig: {} as Record<string, PriceConfig>,
  };

  if (initialConfig.groupBy !== "none") {
    for (const variant of data.variationOptions[initialConfig.groupBy]!) {
      const refProduct = data.products.find((p) => p.variations?.[initialConfig.groupBy] === variant.title);
      result.groupConfig[variant.id] = getDefaultPriceConfig(initialConfig, refProduct?.product_price ?? 0);
    }
    result.groupConfig["unknownVariant"] = getDefaultPriceConfig(
      initialConfig,
      data.products.find((p) => !p.variations?.[initialConfig.groupBy])?.product_price ?? 0,
    );
  } else {
    for (const [asin, products] of Object.entries(data.by_asin)) {
      result.asinConfig[asin] = getDefaultPriceConfig(initialConfig, products?.[0]?.product_price ?? 0);
      for (const product of products ?? []) {
        result.skuConfig[product.sku] = { enabled: false, currentPrice: product.product_price };
      }
    }
  }
  return result;
}

function lookupGroupForProduct(
  product: GVProduct,
  groupBy: string,
  variationOptions: Record<string, { id: string; title: string }[]>,
) {
  return (
    variationOptions[groupBy]?.find((v) => product.variations?.[groupBy] === v.title) ?? {
      id: "unknownVariant",
      title: "Unknown variant",
    }
  );
}

function ParentAsinManagementForm({
  initialConfig,
  data,
}: {
  initialConfig: InitialConfigType;
  data: Exclude<ReturnType<typeof useParentAsinPricingConfig>["data"], undefined>;
}) {
  const initialValues = resolveInitialConfigToFormValues(initialConfig, data);
  const form = useFormily({
    initialValues,
  });

  const groupedProducts = Map.groupBy(data.products, (p) =>
    initialConfig.groupBy === "none" ?
      { id: "none", title: "none" }
    : lookupGroupForProduct(p, initialConfig.groupBy, data.variationOptions),
  );
  return (
    <FormProvider form={form}>
      <FormConsumer>
        {() => (
          <>
            {!!initialConfig.heroAsin && initialConfig.heroAsin !== "none" && (
              <HeroProductCard data={data.by_asin[initialConfig.heroAsin]!} />
            )}

            <Table bordered>
              <tbody>
                <tr>
                  <th colSpan={42}>Pricing Config</th>
                </tr>
                {Array.from(groupedProducts, ([variant, products]) => [variant, products] as const)
                  .sort(([a], [b]) => a.title.localeCompare(b.title))
                  .map(([variant, products]) => (
                    <GroupRow key={variant.id} variant={variant} products={products} />
                  ))}
              </tbody>
            </Table>
          </>
        )}
      </FormConsumer>
    </FormProvider>
  );
}

function HeroProductCard({ data }: { data: GVProduct[] }) {
  const displayProduct = data[0]!;
  return (
    <Card>
      <Card.Header
        className={css({
          paddingTop: "small",
          fontSize: "larger",
          textAlign: "center",
          backgroundColor: "transparent",
        })}
      >
        Hero Product
      </Card.Header>
      <Card.Body>
        <Grid columns={2}>
          <HStack>
            <ProductImageURL url={displayProduct.product_image_url} width={100} height={100} />
            <span className={css({ flex: "1", maxWidth: "500px" })}>
              {`${displayProduct.asin} - ${displayProduct.product_title}`}{" "}
            </span>
          </HStack>
          <ObjectField
            name={`asinConfig.${displayProduct.asin}`}
            component={[PriceConfigForm]}
            initialValue={getDefaultPerPriceType("dynamic", displayProduct.product_price)}
          />
        </Grid>
      </Card.Body>
    </Card>
  );
}

function GroupRow({ variant, products }: { variant: { id: string; title: string }; products: GVProduct[] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const isExpandedResolved = isExpanded || variant.id === "none";
  const by_asin = Object.entries(Object.groupBy(products, (p) => p.asin));
  return (
    <>
      {variant.id !== "none" && (
        <tr
          className={css({
            borderTop: "2px solid {colors.trellisPalette.4}",
            fontSize: "large",
          })}
        >
          <td>
            <button
              onClick={() => setIsExpanded((value) => !value)}
              className={css({ border: "none", background: "transparent" })}
            >
              {isExpanded ?
                <FaCaretDown />
              : <FaCaretRight />}
            </button>
          </td>
          <td>{`${variant.title} (${by_asin.length} asin(s))`}</td>
          <td>
            <ObjectField name={`groupConfig.${variant.id}`} component={[PriceConfigForm]} />
          </td>
        </tr>
      )}
      {isExpandedResolved &&
        by_asin.map(([asin, skus]) => {
          return <AsinRow key={asin} skus={skus!} />;
        })}
    </>
  );
}

function AsinRow({ skus }: { skus: GVProduct[] }) {
  const [isExpanded, setIsExpanded] = useState(false);
  const displayProduct = skus[0]!;
  return (
    <>
      <tr>
        <td className={css({ paddingLeft: "30px" })}>
          <button
            onClick={() => setIsExpanded((value) => !value)}
            className={css({ border: "none", background: "transparent" })}
          >
            {isExpanded ?
              <FaCaretDown />
            : <FaCaretRight />}
          </button>
        </td>
        <td className={css({ width: "30%" })}>
          <Flex gap={4} alignContent={"center"} alignItems={"center"}>
            <ProductImageURL url={displayProduct.product_image_url} width={50} height={50} />
            {`${displayProduct.asin} - ${displayProduct.product_title} (${skus.length} skus)`}
          </Flex>
        </td>
        <td>
          <ObjectField
            name={`asinConfig.${displayProduct.asin}`}
            component={[PriceConfigForm]}
            initialValue={{ enabled: false, currentPrice: displayProduct.product_price }}
          />
        </td>
      </tr>
      {isExpanded && skus.map((sku) => <SkuRow key={sku.id} data={sku} />)}
    </>
  );
}

function SkuRow({ data }: { data: GVProduct }) {
  return (
    <tr>
      <td className={css({ paddingLeft: "60px" })} colSpan={7}>
        {data.sku} - {data.product_price}
      </td>
    </tr>
  );
}

const getDefaultPerPriceType = (priceType: string, currentPrice: number): PriceConfig => {
  switch (priceType) {
    case "fixed":
      return {
        enabled: true,
        priceType,
        currentPrice,
        fbmOffset: {
          amount: 0,
          type: "currency",
        },
      };
    case "dynamic":
      return {
        enabled: true,
        priceType,
        currentPrice,
        fbmOffset: {
          amount: 0,
          type: "currency",
        },
        sliderValue: 0,
        minPrice: currentPrice * 0.9,
        maxPrice: currentPrice * 1.1,
        additionalConstraints: [],
      };
    case "sameas":
      return {
        enabled: true,
        priceType,
        currentPrice,
        fbmOffset: {
          amount: 0,
          type: "currency",
        },
        targetType: "hero",
        target: "",
        addStatsToLeader: false,
        adjustLeaderPrice: { amount: 0, type: "currency" },
      };
    default:
      throw new Error(`Invalid price type ${priceType}`);
  }
};

const PriceConfigForm = observer(() => {
  const field = useField();

  useFormEffects(() => {
    onFieldValueChange(`${field.address.entire}.priceType`, (priceTypeField) => {
      priceTypeField.query(".currentPrice").take((priceField) => {
        if (!isField(priceField)) return;
        priceField.readPretty = priceTypeField.value !== "fixed";
      });

      if (!isField(priceTypeField.parent)) return;

      const defaults = getDefaultPerPriceType(priceTypeField.value, priceTypeField.parent.value.currentPrice);
      const current = { ...priceTypeField.parent.value };
      Object.assign(priceTypeField.parent.value, defaults, current);
    });

    onFieldValueChange(`${field.address.entire}.enabled`, (enabledField) => {
      if (!isField(enabledField)) return;

      if (enabledField.value && isField(enabledField.parent)) {
        const defaults = getDefaultPerPriceType("fixed", enabledField.parent.value.currentPrice);
        const current = { ...enabledField.parent.value };
        Object.assign(enabledField.parent.value, defaults, current);
      }
    });
  });

  if (!isObjectField(field)) {
    throw new Error("PriceConfigForm must be used with an Object field");
  }
  const value = field.value as PriceConfig;
  if (!value.enabled) {
    return <Field name="enabled" title="Use own config" component={[Checkbox, { labelSide: "right" }]} />;
  }

  switch (value.priceType) {
    case "fixed":
      return <FixedPriceConfigCells />;
    case "dynamic":
      return <DynamicPriceConfigCells />;
    case "sameas":
      return <FollowPriceConfigCells />;
  }
});

function PriceConfigMenu() {
  const configField = useField();
  if (!isField(configField)) throw new Error("PriceConfigMenu must be used with a field");

  return (
    <MenuButton
      label={<FaEllipsisVertical />}
      buttonProps={{ variant: "contentOnly", className: css({ position: "absolute", top: "0", right: "0" }) }}
    >
      <MenuItem
        onAction={() => {
          configField.value.enabled = false;
        }}
      >
        <FaArrowRotateLeft />
        Use parent configuration
      </MenuItem>
    </MenuButton>
  );
}

function FixedPriceConfigCells() {
  return (
    <Grid position={"relative"} columns={2}>
      <PriceConfigMenu />
      <VStack alignItems={"start"}>
        <span
          className={css({
            marginBottom: "-small",
          })}
        >
          Price
        </span>
        <HStack>
          <Field
            name="priceType"
            component={[Select]}
            dataSource={[
              {
                label: "Fixed",
                value: "fixed",
              },
              {
                label: "Dynamic",
                value: "dynamic",
              },
              {
                label: "Same as",
                value: "sameas",
              },
            ]}
          />
          <Field name="currentPrice" component={[CurrencyInput]} />
        </HStack>
        <Field name="fbmOffset" title="Offset FBM by" component={[OffsetInput]} />
      </VStack>
      {/* <HStack>
        <Checkbox value={!!data.temporaryUntil} onChange={() => {}} />
        {` is temporary until `}
        <DatepickerBase value={data.temporaryUntil ?? ""} disabled={!data.temporaryUntil} onChange={() => {}} />
      </HStack> */}
    </Grid>
  );
}

function DynamicPriceConfigCells() {
  const constraintsModal = useModal(ConstraintsManagementModal);
  const configField = useField();
  if (!isField(configField)) return null;

  return (
    <Grid position={"relative"} columns={2}>
      <PriceConfigMenu />
      <VStack alignItems={"stretch"}>
        <span
          className={css({
            marginBottom: "-small",
          })}
        >
          Price
        </span>
        <Wrap gap={4} alignItems={"center"}>
          <Field
            name="priceType"
            component={[Select]}
            dataSource={[
              {
                label: "Fixed",
                value: "fixed",
              },
              {
                label: "Dynamic",
                value: "dynamic",
              },
              {
                label: "Same as",
                value: "sameas",
              },
            ]}
          />
          <Field name="currentPrice" component={[CurrencyInput]} readPretty />
          <LinkButton fontSize={"medium"}>Advanced config</LinkButton>
        </Wrap>
        <Field name="fbmOffset" title="Offset FBM by" component={[OffsetInput]} />
        <Box minWidth={"250px"} padding={"0 25px"}>
          <Field name="sliderValue">
            {(field) => <PlanSlider planMode={field.value} setPlanMode={field.onInput} />}
          </Field>
        </Box>
      </VStack>

      <VStack alignItems="stretch">
        <Center textAlign={"center"}>Constraints</Center>
        <Grid columnGap={"large"} rowGap={"small"} columns={1}>
          <Field name={"minPrice"} title={"Min Price"} component={[CurrencyInput]} />
          <Field name={"maxPrice"} title={"Max Price"} component={[CurrencyInput]} />
        </Grid>

        <LinkButton
          onPress={async () => {
            return await constraintsModal.show({
              initialValues: configField.value,
              onSubmit(values) {
                Object.assign(configField.value, values);
              },
            });
          }}
        >
          {configField.value.additionalConstraints.length ?
            `${configField.value.additionalConstraints.length} additional constraint(s)`
          : "No additional constraints"}
        </LinkButton>
      </VStack>
    </Grid>
  );
}

function FollowPriceConfigCells() {
  return (
    <Grid position={"relative"} columns={2}>
      <PriceConfigMenu />
      <VStack alignItems={"stretch"}>
        <span
          className={css({
            marginBottom: "-small",
          })}
        >
          Price
        </span>
        <HStack>
          <Field
            name="priceType"
            component={[Select]}
            dataSource={[
              {
                label: "Fixed",
                value: "fixed",
              },
              {
                label: "Dynamic",
                value: "dynamic",
              },
              {
                label: "Same as",
                value: "sameas",
              },
            ]}
          />
          <Field
            name="targetType"
            component={[Select]}
            dataSource={[
              { label: "Hero product", value: "hero" },
              { label: "External product", value: "external" },
            ]}
            reactions={(field) => {
              field.query(".target").take((targetField) => {
                targetField.hidden = field.value !== "external";
              });
            }}
          />
          <Field name="currentPrice" component={[CurrencyInput]} readPretty />
        </HStack>
        <Field name="fbmOffset" title="Offset FBM by" component={[OffsetInput]} />
        <Field name="addStatsToLeader" title={"Add stats to leader"} component={[Checkbox]} />
      </VStack>
      <VStack alignItems="stretch">
        <Field name="target" title="Target ASIN" component={[TextField]} />
        <Field name="adjustLeaderPrice" title="Offset from leader" component={[OffsetInput]} />
      </VStack>
    </Grid>
  );
}
